import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import { awaitDispatch } from '@vk-hr-tek/core/redux';
import {
  showCustomNotification,
  showNotification,
} from '@vk-hr-tek/core/notifications';

import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import { Entity } from '@app/types';
import { ThunkExtra, ThunkValuesAndActions } from '@app/store';
import {
  AccountService,
  AddAccountGroupsToUserParams,
  AddAccountGroupUsersParams,
  GetUsersWithAccountGroupsParams,
  GetUsersWithAccountGroupsQuery,
} from '@app/gen/account';

import {
  accountUsersGroupsAdapter,
  SettingsState,
  SettingsWithRootState,
} from '../../settings.state';
import {
  AccountGroupsDto,
  EditAccountUserGroupsDto,
  RemoveAllUserAccountDto,
} from '../../../dto';
import { SettingsAccountMapper } from '../../../mappers';
import { AccountUsersGroupsResponse } from '../../../types';

import {
  getAccountGroups,
  getAccountGroupsWithCount,
  getAccounts,
} from './account.actions';

export const getUsersWithAccountGroups = createAsyncThunk<
  AccountUsersGroupsResponse,
  GetUsersWithAccountGroupsParams & GetUsersWithAccountGroupsQuery,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getUsersWithAccountGroups',
  async (
    { account_id, search, group_id, limit, offset },
    { rejectWithValue, dispatch, getState, extra: { inject } },
  ) => {
    try {
      const accountMapper = inject(SettingsAccountMapper);

      const state = getState();

      if (state.settings.accounts.status === 'idle') {
        await awaitDispatch(dispatch(getAccounts()));
      }

      const result = await inject(AccountService).getUsersWithAccountGroups({
        params: {
          account_id,
        },
        query: {
          search,
          group_id,
          limit,
          offset,
        },
      });

      return accountMapper.processAccountUsersGroups(result);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const removeUserAccountGroups = createAsyncThunk<
  void,
  ThunkValuesAndActions<RemoveAllUserAccountDto>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/removeAllUserAccountGroups',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const accountService = inject(AccountService);

      await accountService.deleteAllUserAccountGroups({
        params: { account_id: values.accountId },
        body: {
          user_id: values.userId,
        },
      });

      dispatch(getAccountGroupsWithCount({ account_id: values.accountId }));

      dispatch(
        getUsersWithAccountGroups({
          ...values.filter,
          group_id: values.filter?.groupId,
          account_id: values.accountId,
        }),
      );

      dispatch(showNotification(`Права доступа успешно удалены`));

      actions.resolve(null);
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось удалить права доступа',
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const addUserGroups = createAsyncThunk<
  void,
  ThunkValuesAndActions<{
    params: AddAccountGroupsToUserParams;
    body: AccountGroupsDto;
  }>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/addAccountGroup',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const accountService = inject(AccountService);
      const { params, body } = values;
      const { groups, user } = body;

      if (!groups || !user) return;

      await accountService.addAccountGroupsToUser({
        params,
        body: {
          group_ids: groups,
          user_id: user,
        },
      });

      dispatch(showNotification('Права доступа успешно выданы'));

      actions.resolve(null);
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось выдать права доступа',
            message: 'Попробуйте, пожалуйста, еще раз',
          },
        ]),
      );
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const addGroupUsers = createAsyncThunk<
  void,
  ThunkValuesAndActions<{
    role?: string;
    params: AddAccountGroupUsersParams;
    body: AccountGroupsDto;
  }>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/addAccountGroup',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const accountService = inject(AccountService);
      const { params, body, role } = values;
      const { group, users } = body;

      if (!group || !users) return;

      await accountService.addAccountGroupUsers({
        params,
        body: {
          group_id: group,
          user_ids: users,
        },
      });

      dispatch(showNotification(`Роль «${role}» успешно выдана`));

      actions.resolve(null);
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: `Не удалось выдать роль «${values.role}»`,
            message: 'Попробуйте, пожалуйста, еще раз',
          },
        ]),
      );
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const editUserGroups = createAsyncThunk<
  void,
  ThunkValuesAndActions<EditAccountUserGroupsDto>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/editUserGroups',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const accountService = inject(AccountService);

      await accountService.updateUserAccountGroups({
        params: {
          account_id: values.accountId,
        },
        body: {
          user_id: values.userId,
          group_ids: values.userGroups,
        },
      });

      dispatch(getAccountGroupsWithCount({ account_id: values.accountId }));

      dispatch(
        getUsersWithAccountGroups({
          ...values.filter,
          group_id: values.filter?.groupId,
          account_id: values.accountId,
        }),
      );

      dispatch(showNotification(`Права доступа успешно изменены`));

      actions.resolve(null);
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось изменить права доступа',
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getAccountUsersGroupCreationInfo = createAsyncThunk<
  void,
  string,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getAccountUsersGroupCreationInfo',
  async (accountId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();

      await Promise.all([
        state.settings.accounts.status === 'idle' &&
          awaitDispatch(dispatch(getAccounts())),
        awaitDispatch(dispatch(getAccountGroups(accountId))),
      ]);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsAccountUsersGroupsReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getUsersWithAccountGroups.pending, (state) => {
    state.accountUsersGroups.status = 'loading';
    state.accountUsersGroups.error = null;
  });
  builder.addCase(getUsersWithAccountGroups.fulfilled, (state, { payload }) => {
    if (payload) {
      accountUsersGroupsAdapter.upsertMany(
        state.accountUsersGroups,
        payload.users,
      );
      state.accountUsersGroups.currentIds = payload.users.map(({ id }) => id);
    }

    state.accountUsersGroups.total = payload.total;
    state.accountUsersGroups.status = 'complete';
    state.accountUsersGroups.error = null;
  });
  builder.addCase(
    getUsersWithAccountGroups.rejected,
    (state, { payload, error }) => {
      state.accountUsersGroups.status = 'failed';
      state.accountUsersGroups.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );

  builder.addCase(getAccountUsersGroupCreationInfo.pending, (state) => {
    state.accountUsersGroupCreation.status = 'loading';
    state.accountUsersGroupCreation.error = null;
  });
  builder.addCase(getAccountUsersGroupCreationInfo.fulfilled, (state) => {
    state.accountUsersGroupCreation.status = 'complete';
    state.accountUsersGroupCreation.error = null;
  });
  builder.addCase(
    getAccountUsersGroupCreationInfo.rejected,
    (state, { payload, error }) => {
      state.accountUsersGroupCreation.status = 'failed';
      state.accountUsersGroupCreation.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );
};
